// The one client the game talks to the board through. Modelled on trivia's
// wrapper: created once, the submission id threaded through `uuid` so the
// outbox files a run under the id the results card minted, and the proof
// provider asked for a Turnstile token before every attempt.
import { useCallback, useEffect, useState } from "react";
import {
  createLeaderboards,
  type Board,
  type Entry,
  type Run,
  type SubmitResult,
} from "@/lib/leaderboards";
import { CLIENT, GAME } from "./board";
import { proofFor } from "./turnstile";

let nextSubmissionId: string | null = null;

const leaderboards = createLeaderboards({
  game: GAME,
  client: CLIENT,
  baseUrl: import.meta.env.VITE_LEADERBOARDS_URL || undefined,
  uuid: () => nextSubmissionId ?? crypto.randomUUID(),
  token: proofFor,
});

// Once at launch: anything the outbox still holds gets another go. A retried
// run takes a fresh token from the corner surface the turnstile module
// creates for itself.
export function flushOutbox(): void {
  void leaderboards.flush();
}

export function submitRun(run: Run, submissionId: string): Promise<SubmitResult> {
  nextSubmissionId = submissionId;
  try {
    return leaderboards.submit(run);
  } finally {
    nextSubmissionId = null;
  }
}

export function useBoard(limit: number) {
  const [board, setBoard] = useState<Board | null | undefined>(undefined);

  const reload = useCallback(() => {
    let live = true;
    void leaderboards.board(limit).then((next) => {
      if (live) setBoard(next);
    });
    return () => {
      live = false;
    };
  }, [limit]);

  useEffect(reload, [reload]);

  const replace = useCallback((entries: Entry[]) => {
    setBoard((current) => (current ? { ...current, entries } : current));
  }, []);

  return { board, reload, replace };
}
